/**
 * BetaWelcomeModal — shown once for NEW users right after MitID login (before onboarding).
 * User picks "Prøv appen nu" (→ onboarding) or "Giv besked når klar" (→ phone → continue).
 * Choice stored in user.beta_preference, phone in user.phone.
 */
import { useState, useCallback, useRef } from 'react'
import { Modal, View, Text, TextInput, Pressable, StyleSheet, Keyboard, Platform } from 'react-native'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { Rocket, ArrowRight, Bell, Phone, Check } from 'lucide-react-native'
import { useAuth } from '../auth/AuthProvider'
import { useFeatureFlags } from '../context/FeatureFlagsContext'
import { apiClient } from '../api/client'
import { trackEvent } from '../lib/posthog'
import { haptics } from '../utils/haptics'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'

export default function BetaWelcomeModal() {
  const { user, isAuthenticated, refreshUser } = useAuth()
  const { isEnabled } = useFeatureFlags()
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<'choice' | 'phone' | 'done'>('choice')
  const [phone, setPhone] = useState('')
  const [dismissed, setDismissed] = useState(false)
  const phoneRef = useRef<TextInput>(null)

  // Only for NEW users before onboarding — existing users already use the app
  const visible =
    !dismissed &&
    isAuthenticated &&
    isEnabled('beta_welcome') &&
    user != null &&
    !user.onboarding_completed &&
    user.beta_preference == null

  const dismiss = useCallback(async () => {
    setDismissed(true)
    await refreshUser?.()
  }, [refreshUser])

  const handleTryNow = useCallback(async () => {
    if (!user?.id || submitting) return
    setSubmitting(true)
    haptics.medium()
    try {
      await apiClient.users.update(user.id, { beta_preference: 'try_now' })
      trackEvent('beta_welcome_choice', { choice: 'try_now' })
    } catch {
      // Continue anyway
    }
    setSubmitting(false)
    dismiss()
  }, [user?.id, submitting, dismiss])

  const handleNotifyMe = useCallback(() => {
    setStep('phone')
    setTimeout(() => phoneRef.current?.focus(), 300)
  }, [])

  const isValidPhone = phone.replace(/\s/g, '').length >= 8

  const handleSubmitPhone = useCallback(async () => {
    Keyboard.dismiss()
    if (!isValidPhone || !user?.id || submitting) return
    setSubmitting(true)
    haptics.medium()
    try {
      await apiClient.users.update(user.id, {
        beta_preference: 'notify_me',
        phone: phone.replace(/\s/g, ''),
      })
      trackEvent('beta_welcome_choice', { choice: 'notify_me', has_phone: true })
    } catch {
      // Show done step anyway
    }
    setSubmitting(false)
    setStep('done')
  }, [phone, isValidPhone, user?.id, submitting])

  if (!visible) return null

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={s.overlay}>
        <View style={s.backdrop} />
        <View style={s.modal}>
          <View style={s.iconWrap}>
            {step === 'done' ? (
              <Check size={32} color={colors.success} />
            ) : (
              <Rocket size={32} color={colors.primary} />
            )}
          </View>

          {step === 'choice' && (
            <>
              <Text style={s.title}>Velkommen til{'\n'}ForbrugerAgenten Beta!</Text>

              <Text style={s.body}>
                Du er med til at forme fremtiden for bedre forbrugeraftaler i Danmark.
                Din feedback er uvurderlig.
              </Text>

              <Pressable
                style={[s.btnPrimary, submitting && s.btnDisabled]}
                onPress={handleTryNow}
                disabled={submitting}
              >
                <Text style={s.btnPrimaryText}>Prøv appen nu</Text>
                <ArrowRight size={18} color={colors.text.inverse} />
              </Pressable>

              <Pressable
                style={[s.btnSecondary, submitting && s.btnDisabled]}
                onPress={handleNotifyMe}
                disabled={submitting}
              >
                <Bell size={16} color={colors.primary} />
                <Text style={s.btnSecondaryText}>Giv mig besked når klar</Text>
              </Pressable>

              <Text style={s.hint}>
                Du kan altid give feedback via knappen i bunden
              </Text>
            </>
          )}

          {step === 'phone' && (
            <>
              <Text style={s.title}>Hvor skal vi skrive?</Text>

              <Text style={s.body}>
                Indtast dit nummer, så sender vi en SMS når appen er klar.
              </Text>

              <View style={s.inputWrap}>
                <Phone size={18} color={colors.text.muted} style={s.inputIcon} />
                <TextInput
                  ref={phoneRef}
                  style={s.input}
                  placeholder="12 34 56 78"
                  placeholderTextColor={colors.text.muted}
                  value={phone}
                  onChangeText={setPhone}
                  keyboardType="phone-pad"
                  autoComplete="tel"
                  returnKeyType="send"
                  onSubmitEditing={handleSubmitPhone}
                />
              </View>

              <Pressable
                style={[s.btnPrimary, (!isValidPhone || submitting) && s.btnDisabled]}
                onPress={handleSubmitPhone}
                disabled={!isValidPhone || submitting}
              >
                <Text style={s.btnPrimaryText}>Giv mig besked</Text>
                <Bell size={18} color={colors.text.inverse} />
              </Pressable>
            </>
          )}

          {step === 'done' && (
            <>
              <Text style={s.title}>Tak!</Text>
              <Text style={s.body}>
                Vi sender dig en SMS når appen er klar.{'\n'}
                Du er også velkommen til at prøve appen allerede nu.
              </Text>

              <Pressable
                style={s.btnPrimary}
                onPress={dismiss}
              >
                <Text style={s.btnPrimaryText}>Fortsæt til appen</Text>
                <ArrowRight size={18} color={colors.text.inverse} />
              </Pressable>
            </>
          )}
        </View>
      </View>
      </KeyboardAvoidingView>
    </Modal>
  )
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modal: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    width: '88%',
    maxWidth: 400,
    padding: spacing.lg,
    paddingTop: spacing.xl,
    paddingBottom: spacing.lg,
    alignItems: 'center',
    ...shadows.lg,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  title: {
    ...typography.h2,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  body: {
    ...typography.body,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
    lineHeight: 22,
  },
  btnPrimary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: 14,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.md,
    width: '100%',
    marginBottom: spacing.sm,
  },
  btnPrimaryText: {
    ...typography.bodyBold,
    color: colors.text.inverse,
  },
  btnSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.background,
    paddingVertical: 14,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    width: '100%',
    marginBottom: spacing.md,
  },
  btnSecondaryText: {
    ...typography.body,
    color: colors.primary,
  },
  btnDisabled: {
    opacity: 0.5,
  },
  hint: {
    ...typography.caption,
    color: colors.text.muted,
    textAlign: 'center',
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    width: '100%',
    marginBottom: spacing.md,
  },
  inputIcon: {
    marginRight: spacing.sm,
  },
  input: {
    flex: 1,
    ...typography.body,
    color: colors.text.primary,
    paddingVertical: 14,
  },
})
